import React, { useEffect, useMemo } from 'react';
import cx from 'classnames';
import { Link } from 'react-router';

import FullWidthSection from '~/components/static/full-width-section';
import LoadingIndicator from '~/components/loading-indicator';
import MainRecommendation from '~/components/seed/main-recommendation';
import AdditionalRecommendations from '~/components/seed/additional-recommendations';
import NoResults from '~/components/seed/no-results';
import { useModal } from '~/features/modals/modal-actions';
import { useProductData } from '~/features/products/products-actions';
import { useSeedRecommendations } from '~/features/seed/seed-actions';
import { useSession } from '~/features/session/session-actions';
import { useFormAnswers } from '~/hooks/use-form-answers';
import { hideGladlyChat } from '~/utils/analytics/plugins/gladly';
import {
  ProductContent,
  grassSeasonMap,
  grassSpeciesMap,
  purposeToProblemMap,
} from '~/components/seed/seed-content';
import { EventType, analyticsBeacon } from '~/utils/analytics';
import { seedFinderRoute, sundayStoreProductDetailsRoute } from '~/routes';
import { centsToFloat } from '~/utils/numbers';
import { urlFor } from '~/sanity/images';
import getChannelAndPrice from '~/utils/get-channel-price';
import { CHANNEL_NON_FUNNEL } from '~/utils/channels';

import styles from '~/components/seed/seed-finder.module.scss';

const SeedFinderResultsPage = () => {
  const { user, isInitializing: isLoadingUser } = useSession();
  const { showModal } = useModal();

  const {
    seedProducts,
    isLoading: isLoadingRecommendations,
    isError,
  } = useSeedRecommendations(user?.uuid);

  const { products, isLoading: isLoadingProductInfo } = useProductData();

  const { formAnswers } = useFormAnswers();
  const seedAnswers = formAnswers?.SEED_BUNDLE?.[0]?.answers; // both warm and cool season answers are stored here, but the first item in the list should be the most recent
  const seedingSquareFootage = seedAnswers?.find(
    ({ question }) => question === 'seeding-sq-footage'
  )?.response;

  const seedingPurpose = seedAnswers?.find(
    ({ question }) => question === 'seeding-area'
  )?.response;
  const seedingPurposeContent = purposeToProblemMap?.[seedingPurpose];

  const grassSpecies = seedAnswers?.find(
    ({ question }) => question === 'grass-species'
  )?.response;
  const grassSpeciesContent = grassSpeciesMap?.[grassSpecies];

  const grassSeason = formAnswers?.SEED_BUNDLE?.[0]?.metaData?.slug;
  const grassSeasonContent = grassSeasonMap?.[grassSeason];

  const isLoading =
    isLoadingUser || isLoadingProductInfo || isLoadingRecommendations;

  /**
   *  Memoizing because this value appears in the dependency array for
   * `SEED_FINDER_RESULTS_VIEWED` useEffect (and it was causing hte event to fire
   * on every render)
   */
  const mappedProducts = useMemo(
    () =>
      seedProducts
        ?.map((rec) => {
          const matchingProduct = products?.find(
            (p) => p.purchaseSku.skuId === rec.skuId
          );

          if (!matchingProduct) {
            console.log('missing product?', rec);
            return null;
          }

          return {
            ...matchingProduct,
            quantity: rec.quantity,
            sku: rec.skuId,
            seedDescription: ProductContent[rec.skuId]?.description,
            features: ProductContent[rec.skuId]?.features,
            lawnFeature: ProductContent[rec.skuId]?.lawnFeature,
            displayName: ProductContent[rec.skuId]?.displayName,
          };
        })
        .filter(Boolean),
    [products, seedProducts]
  );

  useEffect(() => {
    if (!isLoading && seedProducts && seedProducts.length === 0) {
      const eventPayload = {
        email: user?.email,
        itemNames: ['fertilizer pouches'],
        products: [],
        lawnTrouble: seedingPurpose,
      };

      analyticsBeacon.emit(EventType.SEED_FINDER_RESULTS_VIEWED, eventPayload);
    }

    if (!isLoading && seedProducts?.length > 0) {
      const eventPayload = {
        email: user?.email,
        itemNames: mappedProducts?.map(
          (product) => product?.productDetails?.name
        ),
        products: mappedProducts?.map((product) => {
          const { unitPrice } = getChannelAndPrice(CHANNEL_NON_FUNNEL, product);

          return {
            product_name: product.productDetails?.name,
            description: product.productDetails?.shortDescription,
            image_url: urlFor(product.productDetails?.primaryImage)
              .width(240)
              .height(240)
              .fit('max')
              .url(),
            category_name:
              product?.productDetails?.categories?.[0]?.category?.slug,
            subcategory_name:
              product?.productDetails?.categories?.[0]?.subCategory?.slug,
            price: centsToFloat(unitPrice),
            product_sku: product.purchaseSku?.skuId,
            product_url: `https://www.getsunday.com${sundayStoreProductDetailsRoute(
              {
                category:
                  product?.productDetails?.categories?.[0]?.category?.slug,
                slug: product?.productDetails?.slug,
              }
            )}`,
            quantity: product?.quantity,
          };
        }),
        lawnTrouble: seedingPurpose,
      };

      analyticsBeacon.emit(EventType.SEED_FINDER_RESULTS_VIEWED, eventPayload);
    }
  }, [isLoading, mappedProducts, seedProducts, seedingPurpose, user?.email]);

  if (isError) {
    return (
      <div style={{ height: '100vh' }}>
        <div className={cx(styles.errorContainer, 'panel')}>
          <h1>Uh oh...</h1>
          <img
            className={styles.img}
            src="/images/smoking-mower.gif?v1"
            alt="A lawn mower with smoke coming out"
            width="457"
            height="536"
          />
          <p>
            Something went wrong gathering your seed recommendations. Please{' '}
            <Link to={seedFinderRoute()}>go back</Link> and answer the seed quiz
            again.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ height: '100vh' }}>
        <div className={cx(styles.loadingContainer, 'panel')}>
          <h1>Full seed ahead!</h1>
          <p>Your results are almost ready</p>
          <img
            alt="loading seeds"
            src="/images/seed-loader.gif"
            height="185"
            width="185"
          />
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  const showAddedToCartModal = (product) => {
    hideGladlyChat();
    showModal('ADDED_TO_CART', {
      product,
      showRecommendedProducts: false,
    });
  };

  const showProductDetailsModal = (product) => {
    showModal('PRODUCT_DETAIL', {
      product,
      qtySelected: product.quantity,
    });
  };

  return (
    <FullWidthSection first className={styles.seedFinderResultsPage}>
      <div className={styles.seedFinderResultsInner}>
        {mappedProducts?.length === 0 ? (
          <NoResults
            grassSeasonContent={grassSeasonContent}
            grassSpeciesContent={grassSpeciesContent}
          />
        ) : (
          <>
            <MainRecommendation
              product={mappedProducts?.[0]}
              seedingPurposeContent={seedingPurposeContent}
              seedingSquareFootage={seedingSquareFootage}
              showAddedToCartModal={showAddedToCartModal}
              showProductDetailsModal={showProductDetailsModal}
            />
            {mappedProducts?.length > 1 && (
              <AdditionalRecommendations
                products={mappedProducts?.slice(1)}
                seedingPurposeContent={seedingPurposeContent}
                seedingSquareFootage={seedingSquareFootage}
                showAddedToCartModal={showAddedToCartModal}
                showProductDetailsModal={showProductDetailsModal}
              />
            )}
          </>
        )}
      </div>

      <NeedHelpFooter />
    </FullWidthSection>
  );
};

export default SeedFinderResultsPage;

export const NeedHelpFooter = () => (
  <div className={styles.needHelpFooter}>
    <div className={styles.needHelpFooterUpper}>
      <h2>Need help deciding?</h2>
      <p>We’re here for you every step of the way.</p>
    </div>

    <div className={styles.needHelpFooterLower}>
      <img
        alt="Support"
        height={62.3}
        width={62.3}
        src="/icons/expert-support-icon.svg"
      />
      <div className={styles.needHelpFooterInnerText}>
        <p>
          Call or text us: <strong>415.903.6932</strong>
        </p>
        <p>
          Email us: <strong>support@getsunday.com</strong>
        </p>
      </div>
    </div>
  </div>
);
